<template>
  <div
    class="w-62 h-full flex flex-col items-center relative"
    :class="[
      showInstructions || isDarkMode ? 'text-app-color' : 'text-dark-600',
      isHeadquarters ? 'bg-primary' : 'bg-book'
    ]"
  >
    <div v-if="showInstructions" class="absolute w-full h-full z-10" />

    <div id="main-sidebar" class="flex flex-col flex-grow w-62"
         :class="computedClasses"
    >
      <i class="icon-class icon-small_logo text-center" :class="[btnClasses(), isPaperShape ? 'mt-16' : 'mt-20']" @click="backToBooks" />
      <div class="flex flex-col justify-between flex-grow">
        <div class="flex flex-col items-center">
          <ButtonModule
            prefix="icon-projects text-20"
            tooltip-text="Pages"
            is-white-tooltip
            tooltip-position="top-1/2 transform -translate-y-1/2 left-full z-50 ml-20"
            tooltip-classes="text-13 border border-grey-700 px-13 py-9 font-medium rounded-8"
            tooltip-bg-opacity="bg-opacity-80"
            class="relative mt-50 p-7 rounded-6"
            :class="[btnClasses(isBookPage), isDarkMode ? 'bg-book' : 'hover:bg-grey-400']"
            @click="goToProjects"
          />
          <ButtonModule
            prefix="icon-task_board text-20"
            tooltip-text="Task board"
            is-white-tooltip
            tooltip-position="top-1/2 transform -translate-y-1/2 left-full z-50 ml-20"
            tooltip-classes="text-13 border border-grey-700 px-13 py-9 font-medium rounded-8"
            tooltip-bg-opacity="bg-opacity-80"
            class="relative mt-12 p-7 rounded-6"
            :class="[btnClasses(isTaskBoard), isDarkMode ? 'bg-book' : 'hover:bg-grey-400']"
            @click="goToTaskBoard"
          />
          <ButtonModule
            v-if="isTeamAccount"
            prefix="icon-discussion text-20"
            tooltip-text="Discussions"
            is-white-tooltip
            tooltip-position="top-1/2 transform -translate-y-1/2 left-full z-50 ml-20"
            tooltip-classes="text-13 border border-grey-700 px-13 py-9 font-medium rounded-8"
            tooltip-bg-opacity="bg-opacity-80"
            class="relative mt-12 p-7 rounded-6"
            :class="[btnClasses(isDiscussion), isDarkMode ? 'bg-book' : 'hover:bg-grey-400']"
            @click="goToDiscussions"
          />
          <div class="relative rounded-6 mt-12 flex"
               :class="[btnClasses(isWeekPlanner), isDarkMode ? 'bg-book' : 'hover:bg-grey-400']"
               @click="goToWeekPlanner"
          >
            <div v-if="isFreePlan" class="absolute top-0 z-10 -right-5 py-1 px-5 font-semibold bg-brand text-8 rounded-4 cursor-pointer text-white">PRO</div>
            <ButtonModule
              prefix="icon-week_planner text-25"
              tooltip-text="Week planner"
              is-white-tooltip
              tooltip-position="top-1/2 transform -translate-y-1/2 left-full z-50 ml-20"
              tooltip-classes="text-13 border border-grey-700 px-13 py-9 font-medium rounded-8"
              tooltip-bg-opacity="bg-opacity-80"
              padding="p-4"
              class="relative"
            />
          </div>
          <div class="relative mt-12">
            <span v-if="hasUnreadMessages && !chatMutedAt" class="w-6 h-6 rounded-full absolute top-3 z-100 left-24 bg-yellow-500" />
            <ButtonModule
              v-if="isTeamAccount"
              prefix="icon-chat1 text-20"
              tooltip-text="Chat"
              is-white-tooltip
              tooltip-position="top-1/2 transform -translate-y-1/2 left-full z-50 ml-20"
              tooltip-classes="text-13 border border-grey-700 px-13 py-9 font-medium rounded-8"
              tooltip-bg-opacity="bg-opacity-80"
              class="relative p-7 rounded-6"
              :class="[btnClasses(showChat), isDarkMode ? 'bg-book' : 'hover:bg-grey-400']"
              @click.stop="openChat"
            />
          </div>
        </div>

        <div class="flex flex-col items-center">
          <div v-if="isTeamAccount" class="relative mt-10">
            <span
              v-if="hasUnreadNotification.length > 0"
              class="w-6 h-6 rounded-full absolute top-3 z-100 left-24"
              :class="hasUnreadNotification.length <= 2 ? 'bg-yellow-500' : 'bg-error'"
            />
            <ButtonModule
              prefix="icon-inbox text-20"
              tooltip-text="Inbox"
              is-white-tooltip
              tooltip-position="top-1/2 transform -translate-y-1/2 left-full z-50 ml-20"
              tooltip-classes="text-13 border border-grey-700 px-13 py-9 font-medium rounded-8"
              tooltip-bg-opacity="bg-opacity-80"
              class="relative p-7 rounded-6"
              :class="[btnClasses(isInbox), isDarkMode ? 'bg-book' : 'hover:bg-grey-400']"
              @click="goToInbox"
            />
          </div>
          <ButtonModule
            prefix="icon-search"
            tooltip-text="Search"
            is-white-tooltip
            tooltip-position="top-1/2 transform -translate-y-1/2 left-full z-50 ml-20"
            tooltip-classes="text-13 border border-grey-700 px-13 py-9 font-medium rounded-8"
            tooltip-bg-opacity="bg-opacity-80"
            class="relative p-9 rounded-6 mt-10"
            :class="[btnClasses(), isDarkMode ? 'bg-book' : 'hover:bg-grey-400']"
            @click="openSearchModal"
          >
            <template #tooltip>
              <div>
                <span class="mr-5">Search</span>
                <span class="bg-project px-7 py-3 rounded-8 text-12">Ctrl K</span>
              </div>
            </template>
          </ButtonModule>
          <ButtonModule
            prefix="icon-back text-20"
            tooltip-text="Back to spaces"
            is-white-tooltip
            tooltip-position="top-1/2 transform -translate-y-1/2 left-full z-50 ml-20"
            tooltip-classes="text-13 border border-grey-700 px-13 py-9 font-medium rounded-8"
            tooltip-bg-opacity="bg-opacity-80"
            class="relative p-7 rounded-6"
            :class="[btnClasses(), isDarkMode ? 'bg-book' : 'hover:bg-grey-400']"
            @click="backToBooks"
          />
        </div>
      </div>
    </div>
    <WeekPlannerPopup ref="weekPlannerPopupRef" />
    <Chat
      v-if="isTeamAccount"
      :show-chat="showChat"
      @close-chat="closeChat"
    />
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import localStorageService from '@/core/local-storage'
import { preventBodyScroll } from '@/core/helper-functions'

import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import Chat from '@/components/chat/Chat'
import WeekPlannerPopup from '@/components/week-planner/WeekPlannerPopup'

export default defineComponent({
  components: { ButtonModule, Chat, WeekPlannerPopup },

  props: {
    showInstructions: Boolean
  },

  setup () {
    const { state, getters, commit } = useStore()
    const route = useRoute()
    const router = useRouter()

    const weekPlannerPopupRef = ref(null)

    const book = computed(() => state.book.book)
    const books = computed(() => state.book.books)
    const team = computed(() => state.team.team)
    const headquarters = computed(() => state.team.headquarters)

    const isTeamAccount = computed(() => getters.isTeamAccount)
    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const isFreePlan = computed(() => getters.isFreePlan)
    const isPaperShape = computed(() => getters.isPaperShape)

    const chatMutedAt = computed(() => state.chat.chatMutedAt)
    const hasUnreadMessages = computed(() => state.chat.hasUnreadMessages)
    const hasUnreadNotification = computed(() => {
      const personal = state.inbox.notifications.personal.filter(item => item.status === 'unread')
      const general = state.inbox.notifications.general.filter(item => item.status === 'unread')
      return [...personal, ...general]
    })

    const isHeadquarters = computed(() => route.name === 'Headquarters')
    const isBookPage = computed(() => route.name === 'Book' || route.name === 'TeamBook' || route.name === 'Headquarters')
    const isTaskBoard = computed(() => route.name === 'TaskBoard' || route.name === 'TaskBoardTeam')
    const isWeekPlanner = computed(() => route.name === 'WeekPlanner' || route.name === 'WeekPlannerTeam')
    const isDiscussion = computed(() => route.name === 'Discussions' || route.name === 'Discussion')
    const isInbox = computed(() => route.name === 'Inbox')
    const showChat = ref(false) // TODO: move to store and use for prevent hot key on discussion page

    watch(() => route.query.openChat, value => {
      showChat.value = !!value
    })

    onMounted(() => {
      showChat.value = !!route.query.openChat
    })

    const btnClasses = computed(() => value => {
      const prefix = value ? '' : 'hover:'
      const lightModeClasses = value ? 'border border-brand text-brand bg-grey-200 shadow-sidebar' : 'border border-transparent'

      return isDarkMode.value ? `${prefix}bg-project ${prefix}text-white border border-transparent` : lightModeClasses
    })

    const computedClasses = computed(() => {
      const border = isDarkMode.value ? 'border-project' : 'border-grey-575'
      const bg = isDarkMode.value ? 'bg-canvas' : 'bg-white shadow-sidebarPaperShape'

      return isPaperShape.value
        ? `border ${border} ${bg} pb-16 my-13 px-9 ml-19 rounded-10`
        : `border-r ${border} ${bg} pb-10 px-13`
    })

    function goToProjects () {
      if (isBookPage.value) return

      if (isTeamAccount.value) {
        router.push({ name: 'TeamBook', params: { team: team.value.link, id: route.params.id } })
      } else {
        router.push({ name: 'Book', params: { id: route.params.id } })
      }
    }

    function goToWeekPlanner () {
      if (isFreePlan.value) {
        weekPlannerPopupRef.value.open()
        return
      }

      if (book.value?.id) {
        if (isTeamAccount.value) {
          router.push({ name: 'WeekPlannerTeam', params: { team: team.value.link, id: book.value.id }, query: { spaces: book.value.id } })
        } else {
          router.push({ name: 'WeekPlanner', params: { id: book.value.id }, query: { spaces: book.value.id } })
        }
      } else {
        let bookIds = books.value.filter(b => !b.isSection).map(b => b.id).join(',')
        if (isTeamAccount.value) {
          bookIds += `,${headquarters.value.id}`
          router.push({ name: 'WeekPlannerTeam', params: { team: team.value.link, id: route.params.id }, query: { spaces: bookIds } })
        } else {
          router.push({ name: 'WeekPlanner', params: { id: route.params.id }, query: { spaces: bookIds } })
        }
      }
    }

    function goToTaskBoard () {
      if (book.value?.id) {
        if (isTeamAccount.value) {
          router.push({ name: 'TaskBoardTeam', params: { team: team.value.link, id: book.value.id }, query: { spaces: book.value.id } })
        } else {
          router.push({ name: 'TaskBoard', params: { id: book.value.id }, query: { spaces: book.value.id } })
        }
      } else {
        let bookIds = books.value.filter(b => !b.isSection).map(b => b.id).join(',')
        if (isTeamAccount.value) {
          bookIds += `,${headquarters.value.id}`
          router.push({ name: 'TaskBoardTeam', params: { team: team.value.link, id: route.params.id }, query: { spaces: bookIds } })
        } else {
          router.push({ name: 'TaskBoard', params: { id: route.params.id }, query: { spaces: bookIds } })
        }
      }
    }

    function goToDiscussions () {
      if (book.value?.id) {
        router.push({ name: 'Discussions', params: { team: team.value.link, id: book.value.id }, query: { spaces: book.value.id } })
      } else {
        let bookIds = books.value.filter(b => !b.isSection).map(b => b.id).join(',')
        bookIds += `,${headquarters.value.id}`
        router.push({ name: 'Discussions', params: { team: team.value.link, id: route.params.id }, query: { spaces: bookIds } })
      }
    }

    function goToInbox () {
      const bookId = book.value?.id || route.params.id

      router.push({ name: 'Inbox', params: { team: team.value.link, id: bookId } })
    }

    function backToBooks () {
      localStorageService.removeItem('lastOpenedProject')

      isTeamAccount.value
        ? router.push({ name: 'TeamBooks', params: { team: team.value.link } })
        : router.push({ name: 'Books' })

      if (isMobile.value) {
        commit('updateSidebar', false)
      }
    }

    function openSearchModal () {
      commit('toggleSearch', true)
    }

    function openChat () {
      preventBodyScroll(true)
      showChat.value = true
      // dispatch('updateMessageStatus', { chatId: book.value.chat.id })
    }

    function closeChat () {
      preventBodyScroll(false)
      showChat.value = false

      const query = Object.assign({}, route.query)
      delete query.openChat
      router.replace({ query })
    }

    return {
      // ref
      weekPlannerPopupRef,
      // data
      // computed
      isInbox,
      showChat,
      isBookPage,
      isFreePlan,
      isDarkMode,
      btnClasses,
      isTaskBoard,
      chatMutedAt,
      isDiscussion,
      isPaperShape,
      isTeamAccount,
      isWeekPlanner,
      isHeadquarters,
      computedClasses,
      hasUnreadMessages,
      hasUnreadNotification,

      // function
      openChat,
      goToInbox,
      closeChat,
      backToBooks,
      goToProjects,
      goToTaskBoard,
      goToWeekPlanner,
      openSearchModal,
      goToDiscussions
    }
  }
})
</script>

<style scoped lang="scss">
.icon-class {
  @apply block p-7 cursor-pointer rounded-6 text-20;
}
</style>
